/**
 * Display formatting. This module is the *only* place rounding happens —
 * "round at display boundaries only, never mid-calculation". ADR-0002.
 */
import { formatCents } from '@picklii/recon';
import type { Finding, SeverityBand } from '@picklii/recon';

export function money(cents: number | null): string {
	return cents === null ? 'Unknown' : formatCents(cents);
}

/** Hours are decimal; two places, and only here. */
export function hours(value: number | null): string {
	return value === null ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function percent(fraction: number | null): string {
	return fraction === null ? '—' : `${Math.round(fraction * 100)}%`;
}

export function signedHours(value: number): string {
	const formatted = hours(Math.abs(value));
	return value > 0 ? `+${formatted}` : value < 0 ? `−${formatted}` : formatted;
}

/**
 * The score is severity, never "dollars at risk".
 *
 * It is deliberately rendered WITHOUT a currency symbol. The score is exposure
 * scaled by burn and by a 1.5x direction multiplier, so it is not a literal
 * dollar amount — ADR-0009 says in as many words that showing it as one will
 * get it misread by finance. Its magnitude is still dollar-derived and
 * comparable across findings, which is why it keeps that scale rather than
 * being normalised to 0-100.
 */
export function severity(score: number | null): string {
	if (score === null) return 'Not scored';
	return Math.round(score / 100).toLocaleString('en-US');
}

export function bandClass(band: SeverityBand | null): string {
	switch (band) {
		case 'Critical':
			return 'badge-error';
		case 'High':
			return 'badge-warning';
		case 'Medium':
			return 'badge-info';
		case 'Low':
			return 'badge-ghost';
		default:
			return 'badge-ghost';
	}
}

/** The Dashboard project card's left-edge color stripe. Same band→color
 * mapping as `bandClass`, in the `border-l-*` form a card border needs. */
export function bandStripeClass(band: SeverityBand | null): string {
	switch (band) {
		case 'Critical':
			return 'border-l-error';
		case 'High':
			return 'border-l-warning';
		case 'Medium':
			return 'border-l-info';
		default:
			return 'border-l-base-300';
	}
}

/** The Dashboard project card's burn meter. Same mapping again, in daisyUI's
 * `progress-*` modifier form. */
export function bandProgressClass(band: SeverityBand | null): string {
	switch (band) {
		case 'Critical':
			return 'progress-error';
		case 'High':
			return 'progress-warning';
		case 'Medium':
			return 'progress-info';
		default:
			return '';
	}
}

export const FINDING_LABELS: Record<Finding['type'], string> = {
	PROJECT_MISSING_IN_PSA: 'Project missing in PSA',
	ROLE_MISSING_IN_PSA: 'Role missing in PSA',
	ROLE_MISSING_IN_SOLD: 'Role not sold',
	HOURS_MISMATCH: 'Hours mismatch',
};

export const RATE_SOURCE_LABELS: Record<Finding['rateSource'], string> = {
	exact: 'From the SOW',
	same_client_same_role: 'Estimated — same role, same client',
	dataset_role_weighted_average: 'Estimated — same role, weighted average',
	project_blended: "Estimated — project's blended rate",
	unknown: 'No rate available',
};

export function shortDate(iso: string): string {
	if (iso === '') return '—';
	const date = new Date(iso);
	return Number.isNaN(date.getTime())
		? iso
		: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
